import { Injectable, inject, Renderer2, RendererFactory2 } from '@angular/core';
import { Workbook, Worksheet } from 'exceljs';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { ReportContentTable, SampleAnalyses } from '../../models/shared';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFWorkbookRenderService {
    private static readonly bodyStartColumnNo = 3; // C
    private static readonly bodyEndColumnNo = 12; // L
    private static readonly defaultColumnWidth = 8.43;
    private static readonly bodyWidthSafetyFactor = 0.85;
    private static readonly cellTextPaddingInPixels = 8;
    private static readonly bodyIndent = 3;
    private static readonly bodyIndentInPixels = 20;

    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);
    private _renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
    private _textMeasureContext?: CanvasRenderingContext2D | null;

    async GenerateCenteredSection(workbook: Workbook, worksheetName: string, subHeader: string, content: string, subContent = '', fontSizeTitle = 24, fontSizeSubTitle = 16) {
        try {
            const sampleInfoWorksheet = workbook.addWorksheet(worksheetName);

            sampleInfoWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(sampleInfoWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.landscape, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 80,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(sampleInfoWorksheet, subHeader, reportConstants.d1, reportConstants.m1);

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            sampleInfoWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            sampleInfoWorksheet.mergeCells(reportConstants.b12, reportConstants.n13);
            const textCell = sampleInfoWorksheet.getCell(reportConstants.b12);
            textCell.value = content;
            textCell.font = { name: reportConstants.eMprintCondensed,size: fontSizeTitle, bold: true };
            textCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            if (subContent !== '') {
                sampleInfoWorksheet.mergeCells(reportConstants.b14, reportConstants.n15);
                const subContentCell = sampleInfoWorksheet.getCell(reportConstants.b14);
                subContentCell.value = subContent;
                subContentCell.font = { name: reportConstants.eMprintCondensed,size: fontSizeSubTitle, bold: false };
                subContentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            }

            this.AddOuterBorder(sampleInfoWorksheet, 4, 26, 2, 14);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateTableOfContents(workbook: Workbook, tableOfContentsWorksheet: Worksheet, tableItem: ReportContentTable[], wellDetails: string[], subHeadder: string) {
        try {
            tableOfContentsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];

            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(tableOfContentsWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,// 1 page
                fitToHeight: 1, // 1 page,
                scale: 95,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            tableOfContentsWorksheet.mergeCells(reportConstants.b1, reportConstants.d1);
            const textWellName = tableOfContentsWorksheet.getCell(reportConstants.b1);
            textWellName.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
            textWellName.value = subHeadder;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            tableOfContentsWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            let rowNo = 5;
            tableOfContentsWorksheet.getColumn(reportConstants.b).width = 60;
            const textCell = tableOfContentsWorksheet.getCell(reportConstants.b + rowNo);
            textCell.value = reportConstants.tableOfContent;
            textCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 2;
            const textCell1 = tableOfContentsWorksheet.getCell(reportConstants.c + rowNo);
            textCell1.value = reportConstants.page;
            textCell1.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
            textCell1.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo++;
            const tocItems = tableItem;
            tocItems.forEach(item => {
                const title = item.title;
                const page = item.page;
                const titleCell = tableOfContentsWorksheet.getCell(reportConstants.b + rowNo);
                titleCell.value = title;
                if (page !== '') {
                    titleCell.numFmt = reportConstants.at;
                }
                titleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                titleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };

                const pageCell = tableOfContentsWorksheet.getCell(reportConstants.c + rowNo);
                pageCell.value = page === '' ? '' : Number(page);
                pageCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                pageCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                rowNo++;
            });

            this.AddOuterBorder(tableOfContentsWorksheet, 4, rowNo + 2, 1, 4);

            const sheets: Worksheet[] = workbook.worksheets;
            const index = sheets.indexOf(tableOfContentsWorksheet);

            if (index !== -1) {
                sheets.splice(index, 1);
                sheets.splice(2, 0, tableOfContentsWorksheet);
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateAnalysesMethods(workbook: Workbook, summaryContent: SampleAnalyses[], subHeader: string) {
        try {
            const analysesMethodsWorksheet = workbook.addWorksheet(reportConstants.analysesMethods);
            analysesMethodsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];

            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(analysesMethodsWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0,//fit the worksheet to one page wide
                fitToHeight: 0,// automatic
                scale: 67,
                margins: {
                    left: 0.7,
                    right: 0.7,
                    top: 0.25,
                    bottom: 0.75,
                    header: 0,
                    footer: 0.3
                }
            });

            analysesMethodsWorksheet.getRow(46).addPageBreak();
            analysesMethodsWorksheet.getColumn(reportConstants.b).width = 2;
            // Set before the wrap width is measured so column L counts at its real width
            analysesMethodsWorksheet.getColumn('L').width = 10;

            analysesMethodsWorksheet.mergeCells(reportConstants.d1, reportConstants.k1);
            const textWellName = analysesMethodsWorksheet.getCell(reportConstants.d1);
            textWellName.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
            textWellName.value = subHeader;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            analysesMethodsWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            analysesMethodsWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 47 },
                ext: { width: 151, height: 31 }
            });

            let rowNo = 3;
            analysesMethodsWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.gColumn + rowNo);
            const textCell = analysesMethodsWorksheet.getCell(reportConstants.c + rowNo);
            textCell.value = reportConstants.summaryOfAnalysesMethods;
            textCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, indent: ReportPDFWorkbookRenderService.bodyIndent };

            rowNo += 2;
            const lastRowOfFirstPage = 45;
            const firstRowOfSecondPage = 50;
            const bodyFontSize = 10;
            const bodyRowHeight = 15;

            // Merged cells clip in both directions, so each wrapped line gets its own row and must fit inside C..L.
            const usableWidthInPixels = (this.GetColumnRangeWidthInPixels(
                analysesMethodsWorksheet,
                ReportPDFWorkbookRenderService.bodyStartColumnNo,
                ReportPDFWorkbookRenderService.bodyEndColumnNo
            ) - ReportPDFWorkbookRenderService.cellTextPaddingInPixels - ReportPDFWorkbookRenderService.bodyIndentInPixels)
                * ReportPDFWorkbookRenderService.bodyWidthSafetyFactor;

            const splitOverlongWord = (word: string): string[] => {
                const pieces: string[] = [];
                let piece = '';
                for (const character of word) {
                    if (piece !== '' && this.MeasureTextInPixels(piece + character, bodyFontSize) > usableWidthInPixels) {
                        pieces.push(piece);
                        piece = character;
                    }
                    else {
                        piece += character;
                    }
                }
                if (piece !== '') {
                    pieces.push(piece);
                }
                return pieces;
            };

            const wrapParagraphIntoLines = (paragraph: string): string[] => {
                const lines: string[] = [];
                let currentLine = '';
                const words = paragraph.split(' ').flatMap((word) =>
                    this.MeasureTextInPixels(word, bodyFontSize) > usableWidthInPixels ? splitOverlongWord(word) : [word]
                );
                words.forEach((word) => {
                    const candidate = currentLine === '' ? word : `${currentLine} ${word}`;
                    if (this.MeasureTextInPixels(candidate, bodyFontSize) > usableWidthInPixels && currentLine !== '') {
                        lines.push(currentLine);
                        currentLine = word;
                    }
                    else {
                        currentLine = candidate;
                    }
                });
                if (currentLine !== '') {
                    lines.push(currentLine);
                }
                return lines;
            };

            const writeParagraphLine = (lineRowNo: number, text: string) => {
                analysesMethodsWorksheet.mergeCells(
                    lineRowNo,
                    ReportPDFWorkbookRenderService.bodyStartColumnNo,
                    lineRowNo,
                    ReportPDFWorkbookRenderService.bodyEndColumnNo
                );
                const textParagraphCell = analysesMethodsWorksheet.getCell(lineRowNo, ReportPDFWorkbookRenderService.bodyStartColumnNo);
                textParagraphCell.value = text;
                textParagraphCell.font = { name: reportConstants.eMprintCondensed, size: bodyFontSize, bold: false };
                textParagraphCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: false, indent: ReportPDFWorkbookRenderService.bodyIndent };
                analysesMethodsWorksheet.getRow(lineRowNo).height = bodyRowHeight;
            };

            const moveToValidPageStart = (currentRow: number): number => {
                // Row 46 carries the page-2 header, so content resumes at row 50
                if (currentRow > lastRowOfFirstPage && currentRow < firstRowOfSecondPage) {
                    return firstRowOfSecondPage;
                }
                return currentRow;
            };
            summaryContent.forEach((item) => {
                rowNo = moveToValidPageStart(rowNo);

                analysesMethodsWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.gColumn + rowNo);
                const textTitleCell = analysesMethodsWorksheet.getCell(reportConstants.c + rowNo);
                textTitleCell.value = item.title;
                textTitleCell.font = { name: reportConstants.eMprintCondensed, size: bodyFontSize, bold: true };
                textTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, indent: ReportPDFWorkbookRenderService.bodyIndent };
                analysesMethodsWorksheet.getRow(rowNo).height = bodyRowHeight;

                rowNo++;
                item.content.forEach((paragraph) => {
                    wrapParagraphIntoLines(paragraph).forEach((line) => {
                        rowNo = moveToValidPageStart(rowNo);
                        writeParagraphLine(rowNo, line);
                        rowNo++;
                    });
                    rowNo = moveToValidPageStart(rowNo + 1);
                });
            });


            this.AddOuterBorder(
                analysesMethodsWorksheet,
                3,
                45,
                2,
                12
            );

            this.AddOuterBorder(
                analysesMethodsWorksheet,
                50,
                rowNo,
                2,
                12
            );

            analysesMethodsWorksheet.mergeCells(reportConstants.d48, reportConstants.k48);
            const textWellNameSecond = analysesMethodsWorksheet.getCell(reportConstants.k48);
            textWellNameSecond.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
            textWellNameSecond.value = subHeader;
            textWellNameSecond.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };         
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    private GetColumnRangeWidthInPixels(worksheet: Worksheet, startColumnNo: number, endColumnNo: number): number {
        let widthInPixels = 0;
        for (let columnNo = startColumnNo; columnNo <= endColumnNo; columnNo++) {
            const columnWidth = worksheet.getColumn(columnNo).width ?? ReportPDFWorkbookRenderService.defaultColumnWidth;
            // Excel column width is in character units; the 7px-per-char + 5px padding conversion is the OOXML rule
            widthInPixels += Math.round(columnWidth * 7) + 5;
        }
        return widthInPixels;
    }

    private MeasureTextInPixels(text: string, fontSize: number): number {
        if (this._textMeasureContext === undefined) {
            this._textMeasureContext = typeof document === 'undefined'
                ? null
                : (this._renderer.createElement('canvas') as HTMLCanvasElement).getContext('2d');
        }

        if (!this._textMeasureContext) {
            return text.length * fontSize * 0.62;
        }

        // Measured bold so the regular-weight text Excel renders always has slack, whichever font it substitutes
        this._textMeasureContext.font = `bold ${fontSize}pt "${reportConstants.eMprintCondensed}", Calibri, Arial, sans-serif`;
        return this._textMeasureContext.measureText(text).width;
    }

    private SetDefaultCellStyles(worksheet: Worksheet, rowCount: number, columnCount: number, rowHeight = 18) {
        for (let rowNo = 1; rowNo <= rowCount; rowNo++) {
            const row = worksheet.getRow(rowNo);
            row.height = rowHeight;
            for (let columnNo = 1; columnNo <= columnCount; columnNo++) {
                const cell = row.getCell(columnNo);
                cell.fill = {
                    type: reportConstants.pattern,
                    pattern: reportConstants.solid,
                    fgColor: { argb: reportConstants.ffffffff }
                };
            }
        }
    }

    private AddOuterBorder(worksheet: Worksheet, startRowNo: number, endRowNo: number, startColumnNo: number, endColumnNo: number) {
        for (let rowNo = startRowNo; rowNo <= endRowNo; rowNo++) {
            for (let columnNo = startColumnNo; columnNo <= endColumnNo; columnNo++) {
                const cell = worksheet.getCell(rowNo, columnNo);
                const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                cell.border = {
                    top: rowNo === startRowNo ? { style: reportConstants.thin } : currentTop,
                    bottom: rowNo === endRowNo ? { style: reportConstants.thin } : currentBottom,
                    left: columnNo === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                    right: columnNo === endColumnNo ? { style: reportConstants.thin } : currentRight,
                };
            }
        }
    }
}